import { ChangeDetectorRef, Component, EventEmitter, OnDestroy, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DemandRequest } from '@app/core/models/demand/demand-request';
import { ConfirmDialogService } from '@app/core/services/helpers/dialog.service';
import { FormErrorHandlerService } from '@app/core/services/helpers/form-error-handler.service';
import { ToastHelperService } from '@app/core/services/helpers/toast-helper.service';
import { UtilsService } from '@app/core/services/helpers/utils.service';
import { CompanyService } from '@app/core/services/http/company.service';
import { NdfCalculatorService } from '@app/core/services/http/ndf-calculator.service';
import { nameof } from '@app/core/utils/nameof.utils';
import { VaultBaseComponent } from '@app/shared/components/base/vault-base.component';
import { AppState } from '@app/state';
import { IdValue } from '@interfaces/a-organizar/idValue';
import { Company } from '@interfaces/common/company';
import { InternationalCurrency } from '@interfaces/common/internationalCurrencies';
import { Option } from '@interfaces/common/option';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { ComexHttpService } from '@services/http/comex/comex-operation-http.service';
import { LoanOperationHttpService } from '@services/http/loanOperation/loan-operation-http.service';
import { selectDateFormat } from '@src/app/state/auth/auth.selectors';
import { first } from 'rxjs';

@Component({
  selector: 'app-ndf-edit-demand',
  templateUrl: './ndf-edit-demand.component.html',
  standalone: false,
})
export class NdfEditDemandComponent extends VaultBaseComponent implements OnDestroy {
  // Identifica o demand atual
  demandId: string = '';
  // Formato de data (caso precise)
  dateFormat: string = 'dd/MM/yyyy';

  // Formulário principal
  demandForm: FormGroup;
  public demandRegisterProps = nameof<DemandRequest>();

  // Quando salvar com sucesso, emite evento para o componente pai
  @Output() sendSuccessUpdate: EventEmitter<undefined> = new EventEmitter();

  // Coleções usadas no select
  internationalCurrencies: InternationalCurrency[] = [];
  companyOptions: Option[] = [];
  operationOptions: Option[] = [];
  currencyOptions: Option[] = [];
  comexOperations: Option[] = [];
  loanOperations: Option[] = [];

  // Exemplo de data mínima
  minDueDate: Date | null = null;

  constructor(
    translateService: TranslateService,
    private formBuilder: FormBuilder,
    private ndfCalculatorService: NdfCalculatorService,
    private companyService: CompanyService,
    private utilsService: UtilsService,
    private toast: ToastHelperService,
    private formErrorHandlerService: FormErrorHandlerService,
    private dialogService: ConfirmDialogService,
    private comexHttpService: ComexHttpService,
    private loanOperationService: LoanOperationHttpService,
    private cdr: ChangeDetectorRef,
    store: Store<AppState>
  ) {
    super(translateService, store);

    // Inicializa o form
    this.demandForm = this.initializeDemandForm();

    // Opções fixas
    this.operationOptions = [
      { label: this.t(this.tk.Management.NdfImport), value: 'Import' },
      { label: this.t(this.tk.Management.NdfExport), value: 'Export' },
      { label: this.t(this.tk.Management.NdfLoan), value: 'Loan4131' },
      { label: this.t(this.tk.Management.Indicative), value: 'Indicative' },
    ];
  }

  override ngOnDestroy() {
    super.ngOnDestroy();
  }

  /**
   * Cria um FormGroup vazio
   */
  private initializeDemandForm() {
    console.log('initializeDemandForm');
    return this.formBuilder.group({
      [this.demandRegisterProps.companyId]: ['', [Validators.required]],
      [this.demandRegisterProps.operationType]: ['', [Validators.required]],
      [this.demandRegisterProps.internationalCurrencyId]: [''],
      [this.demandRegisterProps.transactionValue]: ['0'],
      [this.demandRegisterProps.budgetRate]: ['0'],
      [this.demandRegisterProps.dueAt]: [''],
      [this.demandRegisterProps.reference]: [''],
      [this.demandRegisterProps.comexOperationId]: [''],
      [this.demandRegisterProps.loanOperationId]: [''],
    });
  }

  /**
   * Chamado pelo componente pai para abrir a modal com o ID de demanda desejado
   */
  async open(demandId: string) {
    // Guarda o ID e limpa arrays auxiliares
    this.demandId = demandId;
    this.comexOperations = [];
    this.loanOperations = [];
    this.minDueDate = null;

    // Se usar dateFormat do store:
    if (this.store) {
      this.dateFormat = (await this.store
        .select(selectDateFormat)
        .pipe(first())
        .toPromise()) as string;
    }

    // Carrega combos (empresa, currency)
    this.getCompanysOptions();
    this.getCurrencyOptions();

    // Se for para editar (demanda existente), carrega do backend
    if (this.demandId) {
      this.loadDemandData();
    }

    // Abre a modal (sem resetar aqui)
    this.dialogService.confirm(
      '',
      this.t(this.tk.Management.NdfCreateDemand),
      '',
      '',
      '',
      'edit-demand',
      false,
      false
    );
  }

  /**
   * Chamado quando o usuário ou o app fecha a modal
   */
  close(): void {
    console.log('close');
    // 👉 Limpamos o form aqui, garantindo que a cada vez
    // que a modal for reaberta, estará zerado
    this.demandForm.reset();
    this.demandForm = this.initializeDemandForm();

    // Zera variáveis
    this.demandId = '';
    this.minDueDate = null;
    this.comexOperations = [];
    this.loanOperations = [];

    // Força detecção de mudanças e fecha
    this.cdr.detectChanges();
    this.dialogService.close();
  }

  /**
   * Carrega a demanda existente do backend e preenche o form
   */
  private loadDemandData() {
    console.log('loadDemandData');
    this.utilsService.setIsLoading(true);
    this.ndfCalculatorService.getDemandById(this.demandId).subscribe({
      next: demand => {
        this.utilsService.setIsLoading(false);
        if (demand) {
          // Define companyId primeiro para permitir carregar operações relacionadas
          this.demandForm.get(this.demandRegisterProps.companyId)?.setValue(demand.companyId);

          // Preenche o resto do form
          this.demandForm.patchValue({
            [this.demandRegisterProps.operationType]: demand.operationType,
            [this.demandRegisterProps.internationalCurrencyId]: demand.internationalCurrencyId,
            [this.demandRegisterProps.transactionValue]: demand.transactionValue.toString(),
            [this.demandRegisterProps.budgetRate]: demand.budgetRate?.toString(),
            [this.demandRegisterProps.dueAt]: demand.dueAt,
            [this.demandRegisterProps.reference]: demand.reference,
            ...(demand.comexOperationId && { comexOperationId: demand.comexOperationId }),
            ...(demand.loanOperationId && { loanOperationId: demand.loanOperationId }),
          });

          // Agora força a lógica de "Import/Export/Loan/Indicative"
          // para recarregar as combos e validadores
          if (demand.operationType) {
            this.handleOperationTypeChange(demand.operationType);
          }
        }
      },
      error: error => {
        this.utilsService.setIsLoading(false);
        console.error('Error loading demand:', error);
        this.toast.showToastError(
          this.t(this.tk.Management.ErrorLoadingDemand),
          error?.errors?.length ? error.errors[0].message : this.t(this.tk.General.RequestError)
        );
      },
    });
  }

  /**
   * Verifica qual tipo de operação (Import, Export, Loan, Indicative)
   * e ajusta validadores/combos
   */
  private handleOperationTypeChange(operationType: string) {
    console.log('handleOperationTypeChange', operationType);
    if (['Import', 'Export'].includes(operationType)) {
      this.loadComexOperations();
      this.removeIndicativeValidators();
      this.demandForm.get('comexOperationId')?.setValidators([Validators.required]);
      this.demandForm.get('loanOperationId')?.clearValidators();
    } else if (operationType === 'Loan4131') {
      this.loadLoanOperations();
      this.removeIndicativeValidators();
      this.demandForm.get('loanOperationId')?.setValidators([Validators.required]);
      this.demandForm.get('comexOperationId')?.clearValidators();
    } else if (operationType === 'Indicative') {
      // Indicative -> adiciona validadores para esses campos
      this.demandForm
        .get(this.demandRegisterProps.internationalCurrencyId)
        ?.setValidators([Validators.required]);
      this.demandForm
        .get(this.demandRegisterProps.transactionValue)
        ?.setValidators([Validators.required, Validators.min(0)]);
      this.demandForm
        .get(this.demandRegisterProps.budgetRate)
        ?.setValidators([Validators.required, Validators.min(0)]);
      this.demandForm.get(this.demandRegisterProps.dueAt)?.setValidators([Validators.required]);
      this.demandForm.get(this.demandRegisterProps.reference)?.setValidators([Validators.required]);

      // Remove validadores de comex/loan
      this.demandForm.get('comexOperationId')?.clearValidators();
      this.demandForm.get('loanOperationId')?.clearValidators();
    }

    this.demandForm.updateValueAndValidity();
    this.cdr.detectChanges();
  }

  private removeIndicativeValidators(): void {
    console.log('removeIndicativeValidators');
    this.demandForm.get(this.demandRegisterProps.internationalCurrencyId)?.clearValidators();
    this.demandForm.get(this.demandRegisterProps.transactionValue)?.clearValidators();
    this.demandForm.get(this.demandRegisterProps.budgetRate)?.clearValidators();
    this.demandForm.get(this.demandRegisterProps.dueAt)?.clearValidators();
    this.demandForm.get(this.demandRegisterProps.reference)?.clearValidators();
  }

  /**
   * Carrega operações de COMEX (caso operação seja Import/Export)
   */
  loadComexOperations(): void {
    const companyId = this.demandForm.get(this.demandRegisterProps.companyId)?.value;
    if (!companyId) return;

    this.utilsService.setIsLoading(true);
    this.comexHttpService.getAllFiltered({ page: 1, pageSize: 1000, companyId }).subscribe({
      next: response => {
        this.utilsService.setIsLoading(false);
        if (response?.data) {
          this.comexOperations = response.data.map(op => ({
            label: `${op.sequentialId} - ${op.comexNegotiation.reference}`,
            value: op.id,
            data: op,
          }));

          // Se existir valor antigo
          const currentComexOpId = this.demandForm.get('comexOperationId')?.value;
          if (currentComexOpId && this.comexOperations.some(op => op.value === currentComexOpId)) {
            this.demandForm.get('comexOperationId')?.setValue(currentComexOpId);
          }
        }
      },
      error: error => {
        this.utilsService.setIsLoading(false);
        this.toast.showToastError(
          this.t(this.tk.Management.ErrorLoadingOperations),
          error?.errors?.length ? error.errors[0].message : this.t(this.tk.General.RequestError)
        );
      },
    });
  }

  /**
   * Carrega operações de Empréstimo (caso operação seja Loan4131)
   */
  loadLoanOperations(): void {
    const companyId = this.demandForm.get(this.demandRegisterProps.companyId)?.value;
    if (!companyId) return;

    this.utilsService.setIsLoading(true);
    this.loanOperationService.filter({ page: 1, pageSize: 100 }, companyId).subscribe({
      next: response => {
        this.utilsService.setIsLoading(false);
        if (response?.data) {
          this.loanOperations = response.data
            .filter(op => op.id && op.reference)
            .map(op => ({
              label: `${op.sequentialId} - ${op.reference}`,
              value: op.id as string,
              data: op,
            })) as Option[];

          // Se existir valor antigo
          const currentLoanOpId = this.demandForm.get('loanOperationId')?.value;
          if (currentLoanOpId && this.loanOperations.some(op => op.value === currentLoanOpId)) {
            this.demandForm.get('loanOperationId')?.setValue(currentLoanOpId);
          }
        }
      },
      error: error => {
        this.utilsService.setIsLoading(false);
        this.toast.showToastError(
          this.t(this.tk.Management.ErrorLoadingOperations),
          error?.errors?.length ? error.errors[0].message : this.t(this.tk.General.RequestError)
        );
      },
    });
  }

  /**
   * Lidando com inputs numéricos (transactionValue e budgetRate)
   */
  handleInputNumber(inputData: IdValue, key: string): void {
    const value = inputData?.value || 0;
    switch (key) {
      case 'transactionValue':
        this.demandForm.patchValue({
          [this.demandRegisterProps.transactionValue]: value.toString(),
        });
        break;
      case 'budgetRate':
        this.demandForm.patchValue({
          [this.demandRegisterProps.budgetRate]: value.toString(),
        });
        break;
    }
  }

  /**
   * Para datas (dueAt)
   */
  handleCalendarValue(dates: string[], key: string): void {
    if (key === 'dueAt') {
      this.demandForm.patchValue({
        [this.demandRegisterProps.dueAt]: dates.join(','),
      });
    }
  }

  /**
   * Recebe o evento (sendSelectedOption) das <app-select>
   */
  handleOptionChange(selectedOption: Option | null, key: string): void {
    switch (key) {
      case 'companyId':
        this.demandForm.patchValue({
          [this.demandRegisterProps.companyId]: selectedOption?.value,
        });

        // Se já houver operationType selecionado, re-roda a lógica
        if (this.demandForm.get(this.demandRegisterProps.operationType)?.value) {
          this.handleOperationTypeChange(
            this.demandForm.get(this.demandRegisterProps.operationType)?.value
          );
        }
        break;

      case 'operationType':
        this.demandForm.patchValue({
          [this.demandRegisterProps.operationType]: selectedOption?.value,
        });

        // Reseta selects
        this.demandForm.patchValue({
          comexOperationId: null,
          loanOperationId: null,
        });

        // Força a troca de validadores
        if (selectedOption?.value) {
          this.handleOperationTypeChange(selectedOption.value);
        }
        break;

      case 'internationalCurrencyId':
        this.demandForm.patchValue({
          [this.demandRegisterProps.internationalCurrencyId]: selectedOption?.value,
        });
        break;

      case 'comexOperation':
        this.demandForm.patchValue({
          comexOperationId: selectedOption?.value,
        });
        break;

      case 'loanOperation':
        this.demandForm.patchValue({
          loanOperationId: selectedOption?.value,
        });
        break;
    }
  }

  /**
   * Salva as alterações
   */
  saveEditedDemand(): void {
    if (this.demandForm.invalid) {
      this.formErrorHandlerService.markAllAsTouched(this.demandForm);
      return;
    }

    const formValues = this.demandForm.getRawValue();
    const updatedDemand = {
      id: this.demandId,
      companyId: formValues.companyId,
      operationType: formValues.operationType,
      internationalCurrencyId: formValues.internationalCurrencyId,
      transactionValue: parseFloat(formValues.transactionValue),
      budgetRate: parseFloat(formValues.budgetRate),
      dueAt: formValues.dueAt.split('T')[0],
      reference: formValues.reference,
      comexOperationId: formValues.comexOperationId || undefined,
      loanOperationId: formValues.loanOperationId || undefined,
    };

    // Exemplo de PUT
    this.utilsService.setIsLoading(true);
    this.ndfCalculatorService.putDerivativeDemandHeader(updatedDemand).subscribe({
      next: () => {
        this.utilsService.setIsLoading(false);
        // Emite evento para o pai saber que salvou
        this.sendSuccessUpdate.emit();
      },
      error: error => {
        this.utilsService.setIsLoading(false);
        console.error('error: ', error);

        this.formErrorHandlerService.applyFormErrors(this.demandForm, error);
        console.log('demandForm', this.demandForm);
        this.cdr.detectChanges();
      },
    });
  }

  /**
   * Carrega as empresas no combo de empresa
   */
  getCompanysOptions(): void {
    this.utilsService.setIsLoading(true);
    this.companyService.getAllCompanys().subscribe({
      next: response => {
        this.utilsService.setIsLoading(false);
        const data = response?.data || [];
        this.companyOptions = data.map((company: Company) => ({
          label: company.displayName,
          value: company.id,
        })) as Option[];
      },
      error: error => {
        this.utilsService.setIsLoading(false);
        console.error('error: ', error);
        this.toast.showToastError(
          this.t(this.tk.Management.ErrorLoadingCompanys),
          error?.errors?.length ? error.errors[0].message : this.t(this.tk.General.RequestError)
        );
      },
    });
  }

  /**
   * Carrega as moedas internacionais no combo
   */
  private getCurrencyOptions(): void {
    this.utilsService.setIsLoading(true);
    this.ndfCalculatorService.getInternationalCurrencies().subscribe({
      next: response => {
        this.utilsService.setIsLoading(false);
        if (response) {
          this.internationalCurrencies = response;
          this.currencyOptions = response.map((currency: InternationalCurrency) => ({
            label: currency.currencyCode,
            value: currency.id,
          })) as Option[];
        }
      },
      error: error => {
        this.utilsService.setIsLoading(false);
        console.error('Error getting international currencies: ', error);
        this.toast.showToastError(
          this.t(this.tk.Management.ErrorLoadingInternacionalCurrencies),
          error?.errors?.length ? error.errors[0].message : this.t(this.tk.General.RequestError)
        );
      },
    });
  }

  /**
   * Recebe ações do <app-dialog> p/ fechar, etc.
   */
  handleDialogAction(dialogAction: string): void {
    if (!dialogAction) return;

    switch (dialogAction) {
      case 'back':
      case 'close':
        this.close();
        break;
    }
  }

  /**
   * Para o *ngIf no HTML
   */
  get showComexOperations(): boolean {
    return ['Import', 'Export'].includes(
      this.demandForm.get(this.demandRegisterProps.operationType)?.value || ''
    );
  }

  get showLoanOperations(): boolean {
    return this.demandForm.get(this.demandRegisterProps.operationType)?.value === 'Loan4131';
  }

  get showIndicativeFields(): boolean {
    return this.demandForm.get(this.demandRegisterProps.operationType)?.value === 'Indicative';
  }
}
